// Source: https://github.com/ninjatronic/angular-base64/blob/master/angular-base64.js
/*
 * Encapsulation of Nick Galbreath's base64.js library for AngularJS
 * Original notice included below
 */

/*
 * Copyright (c) 2010 Nick Galbreath
 * http://code.google.com/p/stringencoders/source/browse/#svn/trunk/javascript
 *
 * Permission is hereby granted, free of charge, to any person
 * obtaining a copy of this software and associated documentation
 * files (the "Software"), to deal in the Software without
 * restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the
 * Software is furnished to do so, subject to the following
 * conditions:
 *
 * The above copyright notice and this permission notice shall be
 * included in all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
 * EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES
 * OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
 * NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY,
 * WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR
 * OTHER DEALINGS IN THE SOFTWARE.
 */

/* base64 encode/decode compatible with window.btoa/atob
 *
 * window.atob/btoa is a Firefox extension to convert binary data (the "b")
 * to base64 (ascii, the "a").
 *
 * It is also found in Safari and Chrome. It is not available in IE.
 *
 * if (!window.btoa) window.btoa = base64.encode
 * if (!window.atob) window.atob = base64.decode
 *
 * The original spec's for atob/btoa are a bit lacking
 * https://developer.mozilla.org/en/DOM/window.atob
 * https://developer.mozilla.org/en/DOM/window.btoa
 *
 * window.btoa and base64.encode takes a string where charCodeAt is [0,255]
 * If any character is not [0,255], then an exception is thrown.
 *
 * window.atob and base64.decode take a base64-encoded string
 * If the input length is not a multiple of 4, or contains invalid characters
 * then an exception is thrown.
 */

/* tslint:disable:no-bitwise */

export class Base64 {
  private static PADCHAR = '=';
  private static ALPHA = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

  static decode(input: string): string {
    // Convert to string
    input = '' + input;
    let pads: number;
    let i: number;
    let b10: number;
    let imax: number = input.length;

    if (imax === 0) {
      return input;
    }

    if (imax % 4 !== 0) {
      throw new Error('Cannot decode base64');
    }

    pads = 0;
    if (input.charAt(imax - 1) === this.PADCHAR) {
      pads = 1;
      if (input.charAt(imax - 2) === this.PADCHAR) {
        pads = 2;
      }

      // Either way, we want to ignore this last block
      imax -= 4;
    }

    const result: string[] = [];
    for (i = 0; i < imax; i += 4) {
      b10 =
        (this.getByte64(input, i) << 18) |
        (this.getByte64(input, i + 1) << 12) |
        (this.getByte64(input, i + 2) << 6) |
        this.getByte64(input, i + 3);
      result.push(String.fromCharCode(b10 >> 16, (b10 >> 8) & 0xff, b10 & 0xff));
    }

    switch (pads) {
      case 1:
        b10 =
          (this.getByte64(input, i) << 18) | (this.getByte64(input, i + 1) << 12) | (this.getByte64(input, i + 2) << 6);
        result.push(String.fromCharCode(b10 >> 16, (b10 >> 8) & 0xff));
        break;
      case 2:
        b10 = (this.getByte64(input, i) << 18) | (this.getByte64(input, i + 1) << 12);
        result.push(String.fromCharCode(b10 >> 16));
        break;
    }

    return result.join('');
  }

  static encode(input: string): string {
    if (arguments.length !== 1) {
      throw new Error('SyntaxError: Not enough arguments');
    }

    let i: number;
    let b10: number;
    const result: string[] = [];

    // Convert to string
    input = '' + input;

    const imax: number = input.length - (input.length % 3);

    if (input.length === 0) {
      return input;
    }

    for (i = 0; i < imax; i += 3) {
      b10 = (this.getByte(input, i) << 16) | (this.getByte(input, i + 1) << 8) | this.getByte(input, i + 2);
      result.push(this.ALPHA.charAt(b10 >> 18));
      result.push(this.ALPHA.charAt((b10 >> 12) & 0x3f));
      result.push(this.ALPHA.charAt((b10 >> 6) & 0x3f));
      result.push(this.ALPHA.charAt(b10 & 0x3f));
    }

    switch (input.length - imax) {
      case 1:
        b10 = this.getByte(input, i) << 16;
        result.push(this.ALPHA.charAt(b10 >> 18) + this.ALPHA.charAt((b10 >> 12) & 0x3f) + this.PADCHAR + this.PADCHAR);
        break;
      case 2:
        b10 = (this.getByte(input, i) << 16) | (this.getByte(input, i + 1) << 8);
        result.push(
          this.ALPHA.charAt(b10 >> 18) +
            this.ALPHA.charAt((b10 >> 12) & 0x3f) +
            this.ALPHA.charAt((b10 >> 6) & 0x3f) +
            this.PADCHAR
        );
        break;
    }

    return result.join('');
  }

  private static getByte64(input: string, position: number): number {
    const index: number = this.ALPHA.indexOf(input.charAt(position));
    if (index === -1) {
      throw new Error('Cannot decode base64');
    }

    return index;
  }

  private static getByte(input: string, position: number): number {
    const charCode: number = input.charCodeAt(position);
    if (charCode > 255) {
      throw new Error('INVALID_CHARACTER_ERR: DOM Exception 5');
    }

    return charCode;
  }
}
